import type { HardwareAccelerationKind } from './webrtcLatencyContracts';
import type { VideoStreamingStatus } from './native-video/NativeVideoHost';

/**
 * Debug overlay fields for guest (?debug) and host Beacon.
 * Host reports encode; guest reports decode when known.
 */
export type StreamDebugOverlaySnapshot = {
  codecKind: HardwareAccelerationKind;
  framesPerSecond: number | null;
  frameWidth: number;
  frameHeight: number;
  /** Optional raw implementation string, e.g. `OpenH264` or `ExternalEncoder`. */
  implementation?: string;
  /** Optional negotiated codec, e.g. `video/H264 42e01f`. */
  negotiatedCodec?: string;
  /** H.264 profiles the guest offered - what any host-side selection can choose from. */
  offeredProfiles?: string[];
  /** Result of the last send-codec pin attempt. */
  sendCodecPin?: string;
  /** The guest end of the link, when a guest is connected and reporting. */
  guestLink?: GuestLinkDebugSnapshot;
  /** Which video path (Electron / native C++ RTP / native WebCodecs) the viewer is actually on. */
  videoPath?: VideoStreamingStatus;
};

/**
 * What the guest reports about its own end of the stream: the three numbers that decided the
 * 2026-09-20 Android investigation - which codec it is decoding, how deep its jitter buffer is
 * (delay and the target the engine is holding it to), and glass-to-glass latency. Every field
 * is optional: an engine that cannot measure one omits it, and the line shows a dash.
 */
export type GuestLinkDebugSnapshot = {
  engine?: string;
  /** The codec the guest is decoding, e.g. `video/H264`. */
  codec?: string;
  /** Real inbound-rtp jitter-buffer delay this interval, ms. */
  jitterBufferMs?: number;
  /** The engine's jitter-buffer target this interval, ms. */
  jitterBufferTargetMs?: number;
  /** Frames standing between receive and decode. */
  standingFrames?: number;
  /** Network interarrival jitter (RFC 3550), ms - what the buffer should be sized to. */
  jitterMs?: number;
  /** Encoded frame sizes this tick: the estimator's `(max - avg)` term, in bytes. */
  frameAvgBytes?: number;
  frameMaxBytes?: number;
  /** Calibrated glass-to-glass, latest reading, ms. */
  glassToGlassMs?: number;
  /** Calibrated glass-to-glass floor (window minimum), ms. */
  glassToGlassFloorMs?: number;
};

const CODEC_LABEL: Record<HardwareAccelerationKind, string> = {
  hardware: 'HW',
  software: 'SW',
  unknown: '?',
};

/**
 * One-line text for the overlay, e.g.
 * `SW 60fps 1280x720 · video/H264 42e01f · OpenH264`.
 *
 * The codec and implementation are appended when known. Without them a `SW` reading is
 * unactionable: it cannot distinguish H.264 on the software fallback from a codec that has
 * no hardware encoder at all, which is exactly the question that matters when a host with
 * known-good hardware reports software.
 */
export function formatStreamDebugOverlayLine(snapshot: StreamDebugOverlaySnapshot): string {
  const kind = CODEC_LABEL[snapshot.codecKind] ?? '?';
  const fps = snapshot.framesPerSecond === null ? '—' : Number.isFinite(snapshot.framesPerSecond)
    ? Math.max(0, Math.round(snapshot.framesPerSecond))
    : 0;
  const w = Number.isFinite(snapshot.frameWidth) ? Math.max(0, Math.round(snapshot.frameWidth)) : 0;
  const h = Number.isFinite(snapshot.frameHeight) ? Math.max(0, Math.round(snapshot.frameHeight)) : 0;

  const detail = [
    snapshot.negotiatedCodec,
    snapshot.implementation,
    snapshot.offeredProfiles?.length
      ? 'offer[' + snapshot.offeredProfiles.join(',') + ']'
      : undefined,
    snapshot.sendCodecPin ? 'pin:' + snapshot.sendCodecPin : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  return detail
    ? `${kind} ${fps}fps ${w}x${h} · ${detail}`
    : `${kind} ${fps}fps ${w}x${h}`;
}

const ms = (value: number | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}ms` : '—';

/**
 * Second overlay line, the guest's end of the link, e.g.
 * `guest android-chrome · video/VP9 · jb 254/271ms 23f · g2g 285ms (floor 67ms)`.
 *
 * `jb a/b` is delay over target: a target far above a clean link's RTT is the receiver's
 * own jitter estimator standing frames up, and it is the number to look at before anything
 * on the host. Returns `guest —` with no guest snapshot so the line keeps its place.
 */
export function formatGuestLinkDebugLine(guest: GuestLinkDebugSnapshot | undefined): string {
  if (!guest) return 'guest —';
  const num = (value: number | undefined): string =>
    typeof value === 'number' && Number.isFinite(value) ? String(Math.round(value)) : '—';
  const jb =
    guest.jitterBufferMs !== undefined || guest.jitterBufferTargetMs !== undefined
      ? `jb ${num(guest.jitterBufferMs)}/${num(guest.jitterBufferTargetMs)}ms` +
        (guest.standingFrames !== undefined ? ` ${Math.round(guest.standingFrames)}f` : '')
      : undefined;
  const g2g =
    guest.glassToGlassMs !== undefined
      ? `g2g ${ms(guest.glassToGlassMs)}` +
        (guest.glassToGlassFloorMs !== undefined ? ` (floor ${ms(guest.glassToGlassFloorMs)})` : '')
      : undefined;
  const jitter = guest.jitterMs !== undefined ? `jit ${ms(guest.jitterMs)}` : undefined;
  const frames =
    guest.frameAvgBytes !== undefined && guest.frameMaxBytes !== undefined
      ? `fr ${kb(guest.frameAvgBytes)}/${kb(guest.frameMaxBytes)}KB`
      : undefined;
  const parts = [guest.engine ?? '?', guest.codec, jb, jitter, frames, g2g].filter(Boolean);
  return `guest ${parts.join(' · ')}`;
}

const kb = (bytes: number): string => (bytes / 1024).toFixed(bytes >= 10_240 ? 0 : 1);
